// What a league lets you trade, and when — the frontend half.
//
// Client, "League Watchlist: launch behavior": "Right now, you can buy assets
// before the league even starts, and you can buy assets that are not on the
// organizers' list. We need to link what you created in league creation with
// what happens in the league. … We are not trying to invent a new system. We
// just want to use what we have."
//
// What we have is `games.asset_category` and `games.asset_symbols`, written by
// the league wizard and — until now — read only for DISPLAY: the landing page's
// "7 coins" stat, the chart's opening symbol. Nothing on the trading side ever
// consulted them, which is the whole of the defect.
//
// This file is a deliberate MIRROR of app/Support/LeagueAssets.php on the
// backend. The backend is the authority — it is the one an order actually has to
// pass, and a stale tab or a replayed request reaches it whatever the UI did.
// This copy exists because the client asked for an answer at the moment of
// tapping ("show Not eligible in this league"), and a round trip cannot answer a
// tap. Change one, change the other.
//
// The rule, exactly as the wizard writes it:
//
//   • a CUSTOM league stores its tickers in asset_symbols — those, and only
//     those, are eligible.
//   • an "All <category>" league stores an empty asset_symbols and a category,
//     so eligibility is "any symbol of that market".
//   • "mixed", or a league with neither, is unrestricted. That last case is the
//     important one: every league created before those two columns existed looks
//     exactly like it, and this must never retroactively lock their members out.

import { baseSymbol } from "./instrumentFormat";
import { leagueEndAt } from "../pages/Game/landing/leagueFormat";

// The client's own words, so the wording is identical wherever it appears.
export const NOT_ELIGIBLE_MESSAGE = "Not eligible in this league";

// Ours, not the client's — they described trading before the start date as a
// defect but did not supply copy for it.
export const NOT_STARTED_MESSAGE = "This league has not started yet";

// Client, "Completed League Member Access": "Disable trading and new orders
// inside a completed league." Same wording as LeagueAssets::HAS_ENDED on the
// backend, so the ticket and the endpoint refuse in identical words.
export const HAS_ENDED_MESSAGE = "This league has ended";

// Every symbol in this app is stored upper-case; the order ticket already
// normalises with String(ticker).toUpperCase() before it submits one.
export const normalizeSymbol = (symbol) => String(symbol || "").trim().toUpperCase();

// The market a symbol belongs to, from its Polygon prefix. Same split as
// utils/orderFormat.assetClass, but returning the PLURAL key the wizard stores
// in asset_category, so the two can be compared without a translation step.
export const marketOfSymbol = (symbol) => {
  const s = normalizeSymbol(symbol);
  if (s.startsWith("X:")) return "crypto";
  if (s.startsWith("C:")) return "forex";
  return "stocks";
};

const RESTRICTED_CATEGORIES = ["stocks", "crypto", "forex"];

// The SAME asset is stored two ways in this product, and both are correct:
//
//   "X:BTCUSD" — the Polygon pair. What search hands over, so it is what a
//                personal watchlist row holds and what the league wizard writes
//                into asset_symbols today.
//   "BTC"      — the base ticker. What the curated QUSHi rows hold in `symbol`
//                and `etf`, and what older league lists hold in asset_symbols.
//
// Comparing those as strings says Bitcoin is not Bitcoin. That is the client's
// report: "I am in an all crypto league today and I am having trouble using my
// personal Crypto watchlist inside the league. … The QUSHi curated Crypto list
// works correctly. … So the issue seems specific to my personal My Lists >
// Crypto watchlist." The curated list matched the league's entries literally;
// the personal list, holding the pair form of the very same coins, did not.
//
// So a league's list is matched on the BASE asset, not on the spelling. Reuses
// the app-wide formatter rather than a second parser, so "is this the same
// asset?" is answered the same way the chips and titles answer it.
//
// baseSymbol only splits a pair when it KNOWS it is one (an exchange prefix, or
// an explicit crypto/forex category), so an equity is never mis-split: "AAPL"
// stays "AAPL", and a league list of stocks keeps matching exactly as before.
const baseAsset = (symbol) => normalizeSymbol(baseSymbol(normalizeSymbol(symbol)));

// The league's chosen tickers, normalised and de-duplicated. An empty result
// means "no custom list", NOT "nothing is tradable".
export const leagueSymbols = (game) => {
  const raw = Array.isArray(game?.asset_symbols) ? game.asset_symbols : [];
  const out = [];
  raw.forEach((entry) => {
    // The wizard sends plain strings; older payloads sent objects.
    const symbol = normalizeSymbol(typeof entry === "string" ? entry : entry?.symbol);
    if (symbol && !out.includes(symbol)) out.push(symbol);
  });
  return out;
};

export const leagueCategory = (game) =>
  String(game?.asset_category || "").trim().toLowerCase();

// Does this league restrict its universe at all?
export const isRestrictedLeague = (game) => {
  if (!game) return false;
  if (leagueSymbols(game).length) return true;
  return RESTRICTED_CATEGORIES.includes(leagueCategory(game));
};

// The one question: can this symbol be traded in this league?
export const isEligibleInLeague = (game, symbol) => {
  if (!game) return true; // main account — nothing to restrict

  const s = normalizeSymbol(symbol);
  if (!s) return false;

  const symbols = leagueSymbols(game);
  if (symbols.length) {
    // Exact first — the common case, and free.
    if (symbols.includes(s)) return true;
    // Otherwise the same asset written the other way round. See baseAsset.
    const base = baseAsset(s);
    return !!base && symbols.some((entry) => baseAsset(entry) === base);
  }

  const category = leagueCategory(game);
  if (RESTRICTED_CATEGORIES.includes(category)) return marketOfSymbol(s) === category;

  return true; // "mixed", or a league created before these columns existed
};

// The symbol a watchlist row would actually trade.
//
// Curated rows carry a proxy: the "Bitcoin" row has symbol BTC and etf BTC, and
// every screen that opens a chart from a watchlist row uses the etf when one is
// set (watchlistFormat.chartSymbolFor). Eligibility has to test the SAME symbol
// the order would carry, or a row could pass here and be refused at submit.
export const tradeSymbolOf = (item) => {
  if (!item) return "";
  if (item.is_system && item.etf) return normalizeSymbol(item.etf);
  return normalizeSymbol(item.symbol);
};

// The market tabs a league's SEARCH may offer.
//
// Client, "INSIDE A LEAGUE": "The search feature on desktop (asset field) and on
// mobile (magnifying glass) currently lets you bring up an ineligible asset.
// Load the chart, try to buy it, and then you get an error. We need to fix this
// dead end. Anyway, we make the inside of the league search feature a little
// different from the main sites. Only the tabs the league organizer chooses
// appear. Stock, crypto, forex - if the organizer chooses mixed, show all three
// tabs. The organizer picks a customer list; we only show the custom list."
//
// The eligibility rule already existed and the ORDER path already enforced it —
// that is the error the client hits. What was missing is that search never asked,
// so the only place a member learned an asset was out of bounds was after loading
// its chart and pressing Buy. This moves the same answer to the top of the
// funnel.
//
// Returned in the capitalised form both search screens already use for a
// category ("Stocks" / "Crypto" / "Forex"), so neither needs a translation step.
const ALL_MARKET_TABS = ["Stocks", "Crypto", "Forex"];
const TAB_OF_CATEGORY = { stocks: "Stocks", crypto: "Crypto", forex: "Forex" };

export const leagueSearchTabs = (game) => {
  // Main site, a "mixed" league, or one created before these columns existed —
  // every market is in play and search behaves exactly as it always has.
  if (!isRestrictedLeague(game)) return ALL_MARKET_TABS;

  // A custom list: the tabs are whichever markets that list actually spans. A
  // list of five stocks gets one tab, not three empty ones.
  const symbols = leagueSymbols(game);
  if (symbols.length) {
    const present = new Set(symbols.map((symbol) => marketOfSymbol(symbol)));
    return ALL_MARKET_TABS.filter((tab) => present.has(tab.toLowerCase()));
  }

  // An "All <category>" league: exactly that one tab.
  const tab = TAB_OF_CATEGORY[leagueCategory(game)];
  return tab ? [tab] : ALL_MARKET_TABS;
};

// Does this league hand search a fixed list rather than a market to browse?
// "The organizer picks a customer list; we only show the custom list."
export const leagueHasCustomList = (game) => leagueSymbols(game).length > 0;

// Keep only the rows this league can trade. Order preserved.
export const filterEligible = (items, game) => {
  const list = Array.isArray(items) ? items : [];
  if (!isRestrictedLeague(game)) return list;
  return list.filter((item) => isEligibleInLeague(game, tradeSymbolOf(item)));
};

// Has the league's start_date passed? A league with no start date is open.
export const leagueHasStarted = (game, now = new Date()) => {
  if (!game?.start_date) return true;
  const start = new Date(game.start_date);
  // An unparseable date must never lock a league — fail open, the way every
  // other guard in this file does.
  if (Number.isNaN(start.getTime())) return true;
  return start <= now;
};

// Is the league over? `status` is the authoritative answer once the backend
// finaliser has run; leagueEndAt covers the minutes between 11:59 pm and the
// next pass of that finaliser, during which the league is over in every sense
// that matters to a member but has not been claimed yet.
//
// leagueEndAt, not a raw parse of end_date: end_date is stored at MIDNIGHT of
// the final day, so reading it as an instant would refuse orders for the whole
// of the league's last day. See leagueFormat.js.
export const leagueHasEnded = (game, now = new Date()) => {
  if (game?.status === "completed") return true;
  const end = leagueEndAt(game);
  if (!end) return false;
  return end < now;
};

/**
 * Why this league will not take an order for this symbol — or null when it will.
 *
 * One call, so the desktop ticket, the mobile ticket and the watchlist row all
 * refuse for the same reasons in the same words as the backend.
 */
export const leagueTradeBlockReason = (game, symbol) => {
  if (!game) return null;
  if (!leagueHasStarted(game)) return NOT_STARTED_MESSAGE;
  // Added beside the not-started check, not folded into it: a member arriving
  // after the final bell is told the league is over, not that it has not begun.
  if (leagueHasEnded(game)) return HAS_ENDED_MESSAGE;
  if (!isEligibleInLeague(game, symbol)) return NOT_ELIGIBLE_MESSAGE;
  return null;
};
